import * as angular from 'angular';
import './styling.scss';
import * as template from './template.html';

import * as _ from 'lodash';

const module = angular.module('stripe-card', []);

interface StripeCardController extends angular.IStrictController {
    save: () => void;
    stripeKey: string;
    cardType: string;
    hasChangedCardInfo: boolean;
    updateHasChangedCardInfo: (update: boolean) => void;
    callback: (result?: stripe.TokenResponse) => void;
    focusedField: string;
}

const STRIPE_CARD_ERRORS_ELEMENT_SELECTOR = '#card-error';
const CARD_INFO: Array<string> = ['cardNumber', 'cardExpiry', 'cardCvc'];

module.component('stripeCard', {
    bindings: {
        stripeKey: '<',
        updateHasChangedCardInfo: '<',
        callback: '<'
    },
    template: template,
    // TODO: Alvin to convert the following controller to class style
    // disabling this line because it's the last legacy example of a too-big function, so the new standard can be set
    // tslint:disable-next-line:no-big-function
    controller: function(this: StripeCardController, $timeout, $scope: angular.IScope) {
        this.$onInit = () => {
            if (this.updateHasChangedCardInfo) {
                this.updateHasChangedCardInfo(false);
            }

            $scope.$on('stripeCardSave', this.save);

            // Timeout is needed since we need to wait for angular to re-render the component view so that the
            // element used to mount the stripe card is present.
            $timeout(() => {
                setStripeCard(this.stripeKey);
            });
        };

        let stripe: stripe.Stripe;
        let cardNumber: stripe.elements.Element;
        let cardExpiry: stripe.elements.Element;
        let cardCvc: stripe.elements.Element;

        const STRIPE_ELEMENTS_CONFIG = {
            fonts: [
                {
                    family: 'MarkOT',
                    src: 'url("https://myhippo.com/fonts/MarkOT.woff")'
                    // hard-coding font path since URLs have to start with 'https://' or 'data:'.
                }
            ]
        };

        const setStripeCard = (stripeKey: string) => {
            stripe = window.Stripe(stripeKey);
            const elements = stripe.elements(STRIPE_ELEMENTS_CONFIG);

            const elementClasses = {
                focus: 'focus',
                empty: 'empty',
                invalid: 'invalid',
                complete: 'is-complete'
            };

            const elementStyles = {
                base: {
                    iconColor: '#8898AA',
                    color: '#4a4a4a',
                    lineHeight: '40px',
                    fontFamily: 'MarkOT',
                    fontSize: '18px',
                    letterSpacing: '-0.76px',
                    ':focus': {
                        color: '#328df9'
                    },
                    '::placeholder': {
                        color: '#4a4a4a',
                        textAlign: 'left'
                    }
                },
                invalid: {
                    color: '#87949A'
                }
            };

            const eventHandler = (response?: stripe.elements.ElementChangeResponse) => {
                if (response && _.includes(CARD_INFO, response.elementType)) {
                    if (response.brand) {
                        $timeout(() => (this.cardType = response.brand), 0);
                    }
                }

                if (this.updateHasChangedCardInfo) {
                    this.updateHasChangedCardInfo(true);
                }
            };

            const createStripeElement = (
                elementName: stripe.elements.elementsType,
                elementId: string,
                elementPlaceholder: string
            ) => {
                const element = elements.create(elementName, {
                    style: elementStyles,
                    classes: elementClasses,
                    placeholder: elementPlaceholder
                });

                element.mount(elementId);
                element.on('change', eventHandler);

                element.on('focus', () => {
                    $timeout(() => (this.focusedField = elementName), 0);
                    element.update({
                        placeholder: ''
                    });
                });

                element.on('blur', () => {
                    $timeout(() => (this.focusedField = ''), 0);
                    element.update({
                        placeholder: elementPlaceholder
                    });
                });

                return element;
            };

            const cardNumberPlaceholder = 'Card number';
            const cardExpiryPlaceholder = 'MM / YY';
            const cardCvcPlaceholder = '';

            cardNumber = createStripeElement('cardNumber', '#card-number', cardNumberPlaceholder);
            cardExpiry = createStripeElement('cardExpiry', '#card-expiry', cardExpiryPlaceholder);
            cardCvc = createStripeElement('cardCvc', '#card-cvc', cardCvcPlaceholder);
        };

        this.save = () => {
            stripe
                .createToken(cardNumber)
                .then((result) => {
                    // Timeout to force back into angular $apply land
                    $timeout(() => {
                        this.callback(result);
                    }, 0);
                })
                .catch(() => {
                    $timeout(() => {
                        this.callback();
                    }, 0);
                });
        };
    }
});

export { module };
